import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Timestamp } from 'firebase/firestore';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import MaintenanceList from '../../components/maintenance/MaintenanceList';
import MaintenanceForm from '../../components/maintenance/MaintenanceForm';
import MaintenanceFilter from '../../components/maintenance/MaintenanceFilter';
import VehicleFilter from '../../components/vehicle/VehicleFilter';
import { MaintenanceRecord, MaintenanceFilters } from '../../types/maintenance';
import {
  createMaintenanceRecord,
  updateMaintenanceRecord,
  deleteMaintenanceRecord,
} from '../../services/maintenanceService';
import vehicleService from '../../services/vehicleService';
import { Vehicle } from '../../types/vehicle';
import { useNavigate } from 'react-router-dom';
import { getSuppliers } from '../../services/supplierService';

const MaintenanceRecordsPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, currentOrganization } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState<MaintenanceRecord | null>(null);
  const [vehicles, setVehicles] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [filters, setFilters] = useState<MaintenanceFilters>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceProviders, setServiceProviders] = useState<Array<{ id: string; name: string }>>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      if (!currentOrganization) return;

      try {
        const [vehicleList, supplierList] = await Promise.all([
          vehicleService.getVehicles(currentOrganization.id),
          getSuppliers(currentOrganization.id)
        ]);

        setVehicles(
          vehicleList.map((vehicle) => ({
            id: vehicle.id,
            name: `${vehicle.make} ${vehicle.model} - ${vehicle.licensePlate}`,
          }))
        );

        setServiceProviders(
          supplierList
            .map((supplier) => ({
              id: supplier.id || '',
              name: supplier.name,
            }))
            .filter(s => s.id)
        );

      } catch (error) {
        console.error('Error loading data:', error);
        setError(t('maintenance.errors.loading'));
      }
    };

    loadData();
  }, [currentOrganization, t]);

  const handleCreate = () => {
    setSelectedMaintenance(null);
    setIsFormOpen(true);
  };

  const handleEdit = (maintenance: MaintenanceRecord) => {
    setSelectedMaintenance(maintenance);
    setIsFormOpen(true);
  };

  const handleDelete = async (maintenance: MaintenanceRecord) => {
    if (window.confirm(t('maintenance.deleteConfirmation'))) {
      setError(null);
      setSaving(true);
      try {
        await deleteMaintenanceRecord(maintenance.id);
        setRefreshKey(prev => prev + 1); // Force refresh of MaintenanceList
      } catch (error) {
        console.error('Error deleting maintenance record:', error);
        setError(t('maintenance.errors.deleteFailed'));
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSubmit = async (data: Partial<MaintenanceRecord>) => {
    setError(null);
    setSaving(true);
    try {
      if (selectedMaintenance) {
        await updateMaintenanceRecord(selectedMaintenance.id, {
          ...data,
          updatedBy: currentUser?.id || '',
          updatedAt: Timestamp.now(),
        });
      } else {
        if (!data.vehicleId) {
          throw new Error(t('maintenance.form.errors.vehicleRequired'));
        }
        
        const newRecord: Omit<MaintenanceRecord, 'id'> = {
          vehicleId: data.vehicleId,
          organizationId: currentOrganization?.id || '',
          status: data.status || 'pending',
          type: data.type || 'revision',
          openDate: data.openDate || Timestamp.now(),
          closeDate: data.status === 'completed' ? (data.closeDate || Timestamp.now()) : undefined,
          costHT: data.costHT || 0,
          costTTC: data.costTTC || 0,
          description: data.description || '',
          events: [],
          documents: [],
          createdBy: currentUser?.id || '',
          updatedBy: currentUser?.id || '',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };
        await createMaintenanceRecord(newRecord);
      }
      setIsFormOpen(false);
      setSelectedMaintenance(null);
      setRefreshKey(prev => prev + 1); // Force refresh of MaintenanceList
    } catch (error) {
      console.error('Error saving maintenance record:', error);
      setError(t('maintenance.form.errors.submit'));
    } finally {
      setSaving(false);
    }
  };

  if (!currentOrganization) {
    return (
      <Container>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            {t('common.noOrganization')}
          </Typography>
          <Typography color="textSecondary">
            {t('common.organizationRequired')}
          </Typography>
        </Box>
      </Container>
    );
  }


  if (error) {
    return (
      <Container>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            {t('common.retry')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters>
      <Box py={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={3} sx={{ flexGrow: 1 }}>
            <Typography variant="h5" component="h2">
              {t('maintenance.title')}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" sx={{ flexGrow: 1 }}>
              <VehicleFilter
                vehicles={vehicles}
                selectedVehicle={selectedVehicle}
                onVehicleChange={(vehicleId) => {
                  setSelectedVehicle(vehicleId);
                  setFilters((prev: MaintenanceFilters): MaintenanceFilters => ({
                    ...prev,
                    vehicleId: vehicleId || undefined
                  }));
                }}
              />
              <MaintenanceFilter
                filters={filters}
                onFiltersChange={setFilters}
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            {t('maintenance.addNew')}
          </Button>
        </Box>

        <MaintenanceList
          key={refreshKey}
          organizationId={currentOrganization.id}
          onEdit={handleEdit}
          onDelete={handleDelete}
          filters={filters}
        />

        <Dialog
          fullScreen={fullScreen}
          open={isFormOpen}
          onClose={() => !saving && setIsFormOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            <MaintenanceForm
              maintenance={selectedMaintenance || undefined}
              onSubmit={handleSubmit}
              onCancel={() => !saving && setIsFormOpen(false)}
              vehicles={vehicles}
              serviceProviders={serviceProviders}
              disabled={saving}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};

export default MaintenanceRecordsPage;
